import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import "react-quill/dist/quill.snow.css";
import auth from "../../firebase.init";
import Pagination from "../../components/Shared/Pagination";
import { Helmet } from "react-helmet";
import LoadingAnimations from "../../components/Shared/LoadingAnimations";
import { ChevronRight, Plus, Search, SquarePen, Trash2 } from "lucide-react";

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header border-bottom-0">
                        <h5 className="text-24 fw-bold text-dark-300">{title}</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AdminBlogCategories = () => {
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [user] = useAuthState(auth);
    const [modalState, setModalState] = useState({
        isOpen: false,
        type: null // 'add' or 'edit'
    });
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [slugError, setSlugError] = useState("");
    const [formData, setFormData] = useState({
        categoryName: "",
        categoryDetails: "",
        adminEmail: "",
        slug: ""
    });

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const checkSlugExists = (slug, currentId = null) => {
        return categories.some(category =>
            category.slug === slug && category._id !== currentId
        );
    };

    useEffect(() => {
        fetch(`https://server.enjoywiki.com/flip-server/blog-categories`)
            .then((res) => res.json())
            .then((info) => setCategories(info.reverse()));
    }, [user?.email]);

    const filteredCategories = categories.filter((category) =>
        category.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.slug.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = {
            ...formData,
            [name]: value
        };

        if (name === 'categoryName') {
            const newSlug = generateSlug(value);
            updatedFormData.slug = newSlug;

            const currentId = modalState.type === 'edit' ? selectedCategory?._id : null;
            if (checkSlugExists(newSlug, currentId)) {
                setSlugError("This URL already exists. Please use a different category name.");
            } else {
                setSlugError("");
            }
        }

        setFormData(updatedFormData);
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (slugError) return;

        try {
            const categoryData = {
                ...formData,
                adminEmail: user?.email || 'admin@gmail.com'
            };
            const response = await fetch('https://server.enjoywiki.com/flip-server/add-blog-category', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(categoryData),
            });
            const result = await response.json();
            if (result) {
                setCategories([{ ...categoryData, _id: result.insertedId }, ...categories]);
                closeModal();
            }
        } catch (error) {
            console.error('Error adding category:', error);
        }
    };

    const handleEditCategory = async (e) => {
        e.preventDefault();
        if (slugError) return;

        try {
            const categoryData = {
                ...formData,
                adminEmail: user?.email || selectedCategory.adminEmail
            };
            const response = await fetch(`https://server.enjoywiki.com/flip-server/blog-category/${selectedCategory._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(categoryData),
            });
            const result = await response.json();
            if (result) {
                const updatedCategories = categories.map(cat =>
                    cat._id === selectedCategory._id ? { ...cat, ...categoryData } : cat
                );
                setCategories(updatedCategories);
                closeModal();
            }
        } catch (error) {
            console.error('Error updating category:', error);
        }
    };

    const handleDeleteCategory = (userId) => {
        fetch(`https://server.enjoywiki.com/flip-server/blog-category/${userId}`, {
            method: "DELETE",
        })
            .then((res) => res.json())
            .then(() => {
                const updatedCategories = categories.filter((cat) => cat._id !== userId);
                setCategories(updatedCategories);
            });
    };

    const openAddModal = () => {
        setModalState({ isOpen: true, type: 'add' });
        resetForm();
    };

    const openEditModal = (category) => {
        setSelectedCategory(category);
        setFormData({
            categoryName: category.categoryName,
            categoryDetails: category.categoryDetails,
            adminEmail: category.adminEmail,
            slug: category.slug
        });
        setModalState({ isOpen: true, type: 'edit' });
        setSlugError("");
    };

    const closeModal = () => {
        setModalState({ isOpen: false, type: null });
        resetForm();
    };

    const resetForm = () => {
        setFormData({
            categoryName: "",
            categoryDetails: "",
            adminEmail: "",
            slug: ""
        });
        setSlugError("");
        setSelectedCategory(null);
    };

    return (
        <main className="dashboard-main min-vh-100">
            <div className="d-flex flex-column gap-4 pb-110 p-4">
                <Helmet>
                    <title>Categories List | FlipBundle - Manage Your Digital Product Listings</title>
                    <meta name="description" content="View and manage your digital product listings on FlipBundle. Easily update, promote, and track your products for increased sales." />
                </Helmet>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div>
                        <h3 className="text-24 fw-bold text-dark-300 mb-2">Blog Categories List</h3>
                        <ul className="d-flex align-items-center gap-2">
                            <li className="text-dark-200 fs-6">Admin Dashboard</li>
                            <li><ChevronRight size={16} /></li>
                            <li className="text-lime-300 fs-6">All Categories</li>
                        </ul>
                    </div>
                    <div className="d-flex gap-3 align-items-center justify-content-center">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control ps-4"
                                placeholder="Search categories..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <Search
                                className="position-absolute"
                                style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }}
                                size={16}
                            />
                        </div>
                        <button
                            onClick={openAddModal}
                            className="d-flex align-items-center gap-2 w-btn-secondary-lg">
                            <Plus size={16} />
                            Add Blog Category
                        </button>
                    </div>
                </div>

                {/* Table content remains the same */}
                <div className="gig-info-card">
                    <div className="gig-info-body">
                        <div className="overflow-x-auto">
                            <div className="w-100">
                                <table className="w-100 dashboard-table">
                                    <thead>
                                        <tr>
                                            <th scope="col" className="ps-4">Category</th>
                                           
                                            <th scope="col" className="text-center">Edit/Delete</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories === null ? (
                                            <tr>
                                                <td colSpan="5" className="text-center p-4">
                                                    <LoadingAnimations
                                                        type="pulse"
                                                        color="#2563eb"
                                                        size="md"
                                                    />
                                                </td>
                                            </tr>
                                        ) : currentCategories.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center p-4">
                                                    {searchTerm ? "No matching categories found" : "No categories found"}
                                                </td>
                                            </tr>
                                        ) : (
                                            currentCategories.map((category) => (
                                                <tr key={category._id}>
                                                    <td>
                                                        <div className="d-flex gap-3 align-items-center project-name">
                                                            <div>
                                                                {category.categoryName}
                                                                <ul className="d-flex gap-1 order-category mt-1">
                                                                    {category.slug && <li className="text-dark-200">{category.slug}</li>}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    
                                                    <td className="text-center">
                                                    <button
                                                            onClick={() => openEditModal(category)}
                                                            className="btn btn-primary btn-sm"
                                                        >
                                                            <SquarePen size={15} />
                                                            {' '}Edit
                                                        </button> {' '}
                                                        <button
                                                            onClick={() => handleDeleteCategory(category._id)}
                                                            className="btn btn-danger btn-sm"
                                                        >
                                                            <Trash2 size={15} />
                                                            {' '}Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {filteredCategories.length > itemsPerPage && (
                            <div className="pagination-container mt-4 d-flex justify-content-center">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={Math.ceil(filteredCategories.length / itemsPerPage)}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Combined Modal for Add and Edit */}
            <Modal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                title={modalState.type === 'add' ? "Add New Category" : "Edit Category"}
            >
                <form onSubmit={modalState.type === 'add' ? handleAddCategory : handleEditCategory}>
                    <div className="mb-3">
                        <label className="form-label">Category Name</label>
                        <input
                            type="text"
                            name="categoryName"
                            value={formData.categoryName}
                            onChange={handleInputChange}
                            className="form-control"
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Category Details</label>
                        <textarea
                            name="categoryDetails"
                            value={formData.categoryDetails}
                            onChange={handleInputChange}
                            className="form-control"
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Slug (auto-generated)</label>
                        <input
                            type="text"
                            name="slug"
                            value={formData.slug}
                            className="form-control"
                            disabled
                        />
                        {slugError && (
                            <div className="text-danger mt-1">
                                {slugError}
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        className="w-btn-primary-lg w-100"
                        disabled={!!slugError}
                    >
                        {modalState.type === 'add' ? 'Add Category' : 'Update Category'}
                    </button>
                </form>
            </Modal>
        </main>
    );
};

export default AdminBlogCategories;